/**
 * KYCOversikt - Dashboard for kundekontroll (KYC/AML)
 * Viser oversikt over alle klienter og deres KYC-status
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import KYCSkjema from '../components/KYCSkjema';
import './KYCOversikt.css';

export default function KYCOversikt() {
  const [klienter, setKlienter] = useState([]);
  const [kycData, setKycData] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle'); // alle, mangler, pågår, godkjent, avvist
  const [søk, setSøk] = useState('');
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [selectedKlient, setSelectedKlient] = useState(null);
  const [showGodkjennModal, setShowGodkjennModal] = useState(false);
  const [godkjennKYC, setGodkjennKYC] = useState(null);
  const [godkjennForm, setGodkjennForm] = useState({
    godkjent: true,
    risikonivå: 'lav',
    kommentar: ''
  });

  useEffect(() => {
    lastData();
  }, []);

  const lastData = async () => {
    try {
      setLoading(true);

      // Last klienter
      const klienterData = await crmApi.getKlienter();
      const klienterArray = Array.isArray(klienterData) ? klienterData : (klienterData.klienter || []);
      setKlienter(klienterArray);

      // Last KYC-data for alle klienter
      const kycMap = {};
      await Promise.all(
        klienterArray.map(async (klient) => {
          try {
            const response = await fetch(`${crmApi.baseUrl}/api/kyc?klient_id=${klient.id}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });

            if (response.ok) {
              const data = await response.json();
              kycMap[klient.id] = data.kyc;
            }
          } catch (err) {
            console.error(`Kunne ikke laste KYC for klient ${klient.id}:`, err);
          }
        })
      );

      setKycData(kycMap);
    } catch (error) {
      console.error('Feil ved lasting av data:', error);
      alert('Kunne ikke laste data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartKYC = (klient) => {
    setSelectedKlient(klient);
    setShowKYCModal(true);
  };

  const handleKYCFullført = async (egenerklæring) => {
    try {
      const response = await fetch(`${crmApi.baseUrl}/api/kyc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          klient_id: selectedKlient.id,
          egenerklæring
        })
      });

      if (!response.ok) {
        throw new Error('Kunne ikke lagre KYC');
      }

      const result = await response.json();
      alert('✅ KYC-egenerklæring mottatt!\n\n' + result.message);

      setShowKYCModal(false);
      setSelectedKlient(null);
      lastData();
    } catch (error) {
      console.error('Feil ved lagring av KYC:', error);
      alert('Kunne ikke lagre KYC: ' + error.message);
    }
  };

  const handleVisGodkjenn = (klient, kyc) => {
    setSelectedKlient(klient);
    setGodkjennKYC(kyc);
    setGodkjennForm({
      godkjent: true,
      risikonivå: kyc.risikonivå || 'lav',
      kommentar: ''
    });
    setShowGodkjennModal(true);
  };

  const handleGodkjenn = async () => {
    try {
      const response = await fetch(`${crmApi.baseUrl}/api/kyc/${godkjennKYC.id}/godkjenn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(godkjennForm)
      });

      if (!response.ok) {
        throw new Error('Kunne ikke godkjenne KYC');
      }

      const result = await response.json();
      alert('✅ ' + result.message);

      setShowGodkjennModal(false);
      setGodkjennKYC(null);
      setSelectedKlient(null);
      lastData();
    } catch (error) {
      console.error('Feil ved godkjenning:', error);
      alert('Kunne ikke godkjenne KYC: ' + error.message);
    }
  };

  const handleKjørPEPSjekk = async (klient, kyc) => {
    if (!window.confirm(`Kjør PEP-sjekk for ${klient.navn}?\n\nDette søker i OpenSanctions-databasen etter politisk eksponerte personer og sanksjoner.`)) {
      return;
    }

    try {
      const response = await fetch(`${crmApi.baseUrl}/api/kyc/${kyc.id}/pep-sjekk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('PEP-sjekk feilet');
      }

      const result = await response.json();

      let melding = `✅ PEP-sjekk fullført\n\n`;
      melding += `Status: ${result.pep_status}\n`;
      melding += `Antall treff: ${result.antall_treff}\n`;

      if (result.har_pep) {
        melding += `\n⚠️ ADVARSEL: PEP-treff funnet!\n`;
        melding += `Personen er eller har vært politisk eksponert.\n`;
        melding += `Risikonivå satt til HØY.`;
      }

      if (result.har_sanksjon) {
        melding += `\n🚨 KRITISK: Sanksjonstreff funnet!\n`;
        melding += `Personen kan være underlagt internasjonale sanksjoner.`;
      }

      if (result.cached) {
        melding += `\n\n(Brukte cachet resultat)`;
      }

      alert(melding);
      lastData();
    } catch (error) {
      console.error('PEP-sjekk feilet:', error);
      alert('PEP-sjekk feilet: ' + error.message);
    }
  };

  const handleSlettKYC = async (klient, kyc) => {
    if (!window.confirm(`Slett KYC-record for ${klient.navn}?\n\nDette vil permanent fjerne all kundekontroll-data for denne personen.\n\nEr du sikker?`)) {
      return;
    }

    try {
      const response = await fetch(`${crmApi.baseUrl}/api/kyc/${kyc.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Kunne ikke slette KYC');
      }

      alert('✅ KYC-record slettet');
      lastData();
    } catch (error) {
      console.error('Feil ved sletting:', error);
      alert('Kunne ikke slette KYC: ' + error.message);
    }
  };

  const handleSkrivUtPDF = (klient, kyc) => {
    // Åpne i nytt vindu for utskrift
    const printWindow = window.open('', '_blank');
    const dato = new Date().toLocaleDateString('nb-NO');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>KYC-dokument - ${klient.navn}</title>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1f2937; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 30px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
          .info-row { display: flex; margin: 10px 0; }
          .label { font-weight: bold; min-width: 200px; color: #6b7280; }
          .value { color: #1f2937; }
          .status-badge { padding: 4px 12px; border-radius: 12px; display: inline-block; font-size: 14px; }
          .status-godkjent { background: #d1fae5; color: #065f46; }
          .status-pågår { background: #fef3c7; color: #92400e; }
          .status-avvist { background: #fee2e2; color: #991b1b; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>📋 KYC-dokument (Kundekontroll)</h1>

        <div style="margin: 20px 0; padding: 15px; background: #f9fafb; border-radius: 8px;">
          <div class="info-row">
            <div class="label">Generert dato:</div>
            <div class="value">${dato}</div>
          </div>
          <div class="info-row">
            <div class="label">Dokument ID:</div>
            <div class="value">KYC-${kyc.id}</div>
          </div>
        </div>

        <h2>Klientinformasjon</h2>
        <div class="info-row">
          <div class="label">Navn:</div>
          <div class="value">${klient.navn || '-'}</div>
        </div>
        <div class="info-row">
          <div class="label">E-post:</div>
          <div class="value">${klient.epost || '-'}</div>
        </div>
        <div class="info-row">
          <div class="label">Telefon:</div>
          <div class="value">${klient.telefon || '-'}</div>
        </div>
        <div class="info-row">
          <div class="label">Org.nr:</div>
          <div class="value">${klient.org_nr || '-'}</div>
        </div>

        <h2>KYC-status</h2>
        <div class="info-row">
          <div class="label">Status:</div>
          <div class="value">
            <span class="status-badge status-${kyc.status === 'fullført' && kyc.godkjent_av_advokat ? 'godkjent' : kyc.status === 'avvist' ? 'avvist' : 'pågår'}">
              ${kyc.status === 'fullført' && kyc.godkjent_av_advokat ? '✅ Godkjent' : kyc.status === 'avvist' ? '❌ Avvist' : '⏳ Pågår'}
            </span>
          </div>
        </div>
        <div class="info-row">
          <div class="label">Risikonivå:</div>
          <div class="value">${kyc.risikonivå || '-'}</div>
        </div>
        <div class="info-row">
          <div class="label">Egenerklæring signert:</div>
          <div class="value">${kyc.egenerklæring_signert_dato ? new Date(kyc.egenerklæring_signert_dato).toLocaleString('nb-NO') : '-'}</div>
        </div>
        <div class="info-row">
          <div class="label">Godkjent dato:</div>
          <div class="value">${kyc.godkjent_dato ? new Date(kyc.godkjent_dato).toLocaleString('nb-NO') : '-'}</div>
        </div>

        <h2>PEP-sjekk</h2>
        <div class="info-row">
          <div class="label">PEP-sjekk utført:</div>
          <div class="value">${kyc.pep_sjekk_utført_dato ? new Date(kyc.pep_sjekk_utført_dato).toLocaleString('nb-NO') : 'Ikke utført'}</div>
        </div>
        <div class="info-row">
          <div class="label">PEP-status:</div>
          <div class="value">${kyc.pep_status || '-'}</div>
        </div>
        ${kyc.pep_detaljer ? `
        <div class="info-row">
          <div class="label">PEP-detaljer:</div>
          <div class="value">${kyc.pep_detaljer}</div>
        </div>
        ` : ''}

        <div class="footer">
          <p><strong>Nesodden Advokatkontor AS</strong></p>
          <p>Bommenveien 9, 1454 Fagerstrand</p>
          <p>E-post: stig@nesodden-advokat.no | www.nesodden-advokat.no</p>
          <p style="margin-top: 10px;">Dette dokumentet er konfidensieltog underlagt taushet svarplikt.</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    // Vent litt før vi skriver ut
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Statistikk
  const statistikk = {
    totalt: klienter.length,
    mangler: klienter.filter(k => !kycData[k.id]).length,
    pågår: klienter.filter(k => kycData[k.id]?.status === 'pågår').length,
    godkjent: klienter.filter(k => kycData[k.id]?.status === 'fullført' && kycData[k.id]?.godkjent_av_advokat).length,
    avvist: klienter.filter(k => kycData[k.id]?.status === 'avvist').length,
    høyRisiko: klienter.filter(k => kycData[k.id]?.risikonivå === 'høy').length
  };

  // Filtrer klienter
  const filtrerteLienter = klienter.filter(klient => {
    // Filter etter status
    if (filter === 'mangler' && kycData[klient.id]) return false;
    if (filter === 'pågår' && kycData[klient.id]?.status !== 'pågår') return false;
    if (filter === 'godkjent' && !(kycData[klient.id]?.status === 'fullført' && kycData[klient.id]?.godkjent_av_advokat)) return false;
    if (filter === 'avvist' && kycData[klient.id]?.status !== 'avvist') return false;

    // Søk
    if (søk) {
      const søkeLower = søk.toLowerCase();
      return klient.navn?.toLowerCase().includes(søkeLower) ||
             klient.epost?.toLowerCase().includes(søkeLower) ||
             klient.org_nr?.includes(søk);
    }

    return true;
  });

  const getStatusInfo = (klient) => {
    const kyc = kycData[klient.id];

    if (!kyc) {
      return {
        status: 'mangler',
        tekst: 'Ikke påbegynt',
        badge: 'kyc-mangler',
        ikon: '❌'
      };
    }

    if (kyc.status === 'fullført' && kyc.godkjent_av_advokat) {
      return {
        status: 'godkjent',
        tekst: 'Godkjent',
        badge: 'kyc-godkjent',
        ikon: '✅'
      };
    }

    if (kyc.status === 'pågår') {
      return {
        status: 'pågår',
        tekst: 'Venter på godkjenning',
        badge: 'kyc-pågår',
        ikon: '⏳'
      };
    }

    if (kyc.status === 'avvist') {
      return {
        status: 'avvist',
        tekst: 'Avvist',
        badge: 'kyc-avvist',
        ikon: '⚠️'
      };
    }

    return {
      status: 'ukjent',
      tekst: 'Ukjent status',
      badge: 'kyc-mangler',
      ikon: '❓'
    };
  };

  if (loading) {
    return (
      <div className="kyc-oversikt loading">
        <div className="spinner"></div>
        <p>Laster KYC-oversikt...</p>
      </div>
    );
  }

  return (
    <div className="kyc-oversikt">
      <div className="kyc-header">
        <h1>⚖️ Kundekontroll (KYC/AML)</h1>
        <p>Oversikt over alle klienter i henhold til hvitvaskingsloven</p>
      </div>

      {/* Statistikk */}
      <div className="kyc-stats">
        <div className="stat-card">
          <div className="stat-ikon">👥</div>
          <div className="stat-info">
            <div className="stat-verdi">{statistikk.totalt}</div>
            <div className="stat-label">Totalt klienter</div>
          </div>
        </div>

        <div className="stat-card stat-godkjent">
          <div className="stat-ikon">✅</div>
          <div className="stat-info">
            <div className="stat-verdi">{statistikk.godkjent}</div>
            <div className="stat-label">Godkjent KYC</div>
          </div>
        </div>

        <div className="stat-card stat-pågår">
          <div className="stat-ikon">⏳</div>
          <div className="stat-info">
            <div className="stat-verdi">{statistikk.pågår}</div>
            <div className="stat-label">Venter godkjenning</div>
          </div>
        </div>

        <div className="stat-card stat-mangler">
          <div className="stat-ikon">❌</div>
          <div className="stat-info">
            <div className="stat-verdi">{statistikk.mangler}</div>
            <div className="stat-label">Mangler KYC</div>
          </div>
        </div>

        {statistikk.høyRisiko > 0 && (
          <div className="stat-card stat-høyrisiko">
            <div className="stat-ikon">🚨</div>
            <div className="stat-info">
              <div className="stat-verdi">{statistikk.høyRisiko}</div>
              <div className="stat-label">Høy risiko</div>
            </div>
          </div>
        )}
      </div>

      {/* Filter og søk */}
      <div className="kyc-toolbar">
        <div className="filter-buttons">
          <button
            className={filter === 'alle' ? 'active' : ''}
            onClick={() => setFilter('alle')}
          >
            Alle ({statistikk.totalt})
          </button>
          <button
            className={filter === 'mangler' ? 'active' : ''}
            onClick={() => setFilter('mangler')}
          >
            Mangler ({statistikk.mangler})
          </button>
          <button
            className={filter === 'pågår' ? 'active' : ''}
            onClick={() => setFilter('pågår')}
          >
            Pågår ({statistikk.pågår})
          </button>
          <button
            className={filter === 'godkjent' ? 'active' : ''}
            onClick={() => setFilter('godkjent')}
          >
            Godkjent ({statistikk.godkjent})
          </button>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Søk etter klient..."
            value={søk}
            onChange={(e) => setSøk(e.target.value)}
          />
        </div>
      </div>

      {/* Tabell */}
      <div className="kyc-table-container">
        <table className="kyc-table">
          <thead>
            <tr>
              <th>Klient</th>
              <th>Status</th>
              <th>Risikonivå</th>
              <th>PEP-sjekk</th>
              <th>Signert dato</th>
              <th>Godkjent dato</th>
              <th>Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {filtrerteLienter.length === 0 ? (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', padding: '2rem'}}>
                  Ingen klienter funnet
                </td>
              </tr>
            ) : (
              filtrerteLienter.map(klient => {
                const statusInfo = getStatusInfo(klient);
                const kyc = kycData[klient.id];

                return (
                  <tr key={klient.id} className={`kyc-row ${statusInfo.status}`}>
                    <td>
                      <div className="klient-info">
                        <strong>{klient.navn}</strong>
                        {klient.epost && <div className="klient-epost">{klient.epost}</div>}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${statusInfo.badge}`}>
                        {statusInfo.ikon} {statusInfo.tekst}
                      </span>
                    </td>
                    <td>
                      {kyc ? (
                        <span className={`risiko-badge risiko-${kyc.risikonivå}`}>
                          {kyc.risikonivå === 'høy' ? '🔴' : kyc.risikonivå === 'middels' ? '🟡' : '🟢'}
                          {kyc.risikonivå}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {kyc?.pep_sjekk_utført_dato ? (
                        <span className={kyc.pep_status === 'pep' ? 'pep-treff' : 'pep-ok'}>
                          {kyc.pep_status === 'pep' ? '⚠️ PEP' : '✓ OK'}
                        </span>
                      ) : (
                        <span className="text-muted">Ikke kjørt</span>
                      )}
                    </td>
                    <td>
                      {kyc?.egenerklæring_signert_dato ? (
                        formatDato(kyc.egenerklæring_signert_dato)
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {kyc?.godkjent_dato ? (
                        formatDato(kyc.godkjent_dato)
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {!kyc && (
                          <button
                            className="btn-action btn-start"
                            onClick={() => handleStartKYC(klient)}
                            title="Start KYC"
                          >
                            ▶️ Start
                          </button>
                        )}

                        {kyc && kyc.status === 'pågår' && (
                          <>
                            <button
                              className="btn-action btn-godkjenn"
                              onClick={() => handleVisGodkjenn(klient, kyc)}
                              title="Godkjenn KYC"
                            >
                              ✓ Godkjenn
                            </button>
                            <button
                              className="btn-action btn-pep"
                              onClick={() => handleKjørPEPSjekk(klient, kyc)}
                              title="Kjør PEP-sjekk"
                            >
                              🔍 PEP
                            </button>
                          </>
                        )}

                        {kyc && kyc.status === 'fullført' && !kyc.pep_sjekk_utført_dato && (
                          <button
                            className="btn-action btn-pep"
                            onClick={() => handleKjørPEPSjekk(klient, kyc)}
                            title="Kjør PEP-sjekk"
                          >
                            🔍 PEP
                          </button>
                        )}

                        {kyc && (
                          <>
                            <button
                              className="btn-action btn-pdf"
                              onClick={() => handleSkrivUtPDF(klient, kyc)}
                              title="Skriv ut som PDF"
                            >
                              📄 PDF
                            </button>
                            <button
                              className="btn-action btn-slett"
                              onClick={() => handleSlettKYC(klient, kyc)}
                              title="Slett KYC-record"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* KYC Modal */}
      {showKYCModal && selectedKlient && (
        <div className="modal-overlay" onClick={() => setShowKYCModal(false)}>
          <div className="modal-content kyc-modal-wrapper" onClick={(e) => e.stopPropagation()}>
            <KYCSkjema
              klient={selectedKlient}
              onFullført={handleKYCFullført}
              onAvbryt={() => {
                setShowKYCModal(false);
                setSelectedKlient(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Godkjenn Modal */}
      {showGodkjennModal && godkjennKYC && selectedKlient && (
        <div className="modal-overlay" onClick={() => setShowGodkjennModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Godkjenn KYC - {selectedKlient.navn}</h2>

            <div className="form-group">
              <label>
                <input
                  type="radio"
                  checked={godkjennForm.godkjent}
                  onChange={() => setGodkjennForm({...godkjennForm, godkjent: true})}
                />
                ✅ Godkjenn KYC
              </label>
              <label>
                <input
                  type="radio"
                  checked={!godkjennForm.godkjent}
                  onChange={() => setGodkjennForm({...godkjennForm, godkjent: false})}
                />
                ❌ Avvis KYC
              </label>
            </div>

            <div className="form-group">
              <label>Risikonivå</label>
              <select
                value={godkjennForm.risikonivå}
                onChange={(e) => setGodkjennForm({...godkjennForm, risikonivå: e.target.value})}
              >
                <option value="lav">🟢 Lav risiko</option>
                <option value="middels">🟡 Middels risiko</option>
                <option value="høy">🔴 Høy risiko</option>
              </select>
            </div>

            <div className="form-group">
              <label>Kommentar</label>
              <textarea
                value={godkjennForm.kommentar}
                onChange={(e) => setGodkjennForm({...godkjennForm, kommentar: e.target.value})}
                rows="4"
                placeholder="Valgfri kommentar til vurderingen..."
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowGodkjennModal(false)}>
                Avbryt
              </button>
              <button className="btn-primary" onClick={handleGodkjenn}>
                {godkjennForm.godkjent ? '✓ Godkjenn' : '✗ Avvis'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Hjelpefunksjoner
function formatDato(datoString) {
  if (!datoString) return '-';
  const dato = new Date(datoString);
  return dato.toLocaleDateString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}
